import { TaskStatus, isWorkStarted } from './taskWorkflow';

// Mirrors the backend TaskKind. A court case's own rules live in legalCase.js.
export const TaskKind = {
  GENERAL: 'general',
  PERMISSION: 'permission',
  COMPLIANCE: 'compliance',
  LEGAL_CASE: 'legal_case'
};

export const PermissionStage = {
  PREPARATION: 'Preparation',
  SUBMISSION: 'Submission',
  QUERY_RAISED: 'Query raised',
  APPROVAL: 'Approval'
};

export const kindOptions = [
  { label: 'General', value: TaskKind.GENERAL },
  { label: 'Permission', value: TaskKind.PERMISSION },
  { label: 'Compliance', value: TaskKind.COMPLIANCE },
  { label: 'Legal Court Case', value: TaskKind.LEGAL_CASE }
];

// A court case never changes type and no task becomes one (the backend's
// kindChangeError), so editing only swaps General and Permission.
export const editKindOptions = kindOptions.filter(
  (option) => option.value !== TaskKind.LEGAL_CASE
);

export const stageOptions = Object.values(PermissionStage).map((stage) => ({
  label: stage,
  value: stage
}));

// Mirrors the NEXT table in the backend's modules/task/permission.ts: a stage
// may stay put or advance, never go back. Offering a move the API will reject
// only produces a 400 the user cannot act on.
const NEXT_STAGES = {
  [PermissionStage.PREPARATION]: [PermissionStage.PREPARATION, PermissionStage.SUBMISSION],
  [PermissionStage.SUBMISSION]: [
    PermissionStage.SUBMISSION,
    PermissionStage.QUERY_RAISED,
    PermissionStage.APPROVAL
  ],
  [PermissionStage.QUERY_RAISED]: [PermissionStage.QUERY_RAISED, PermissionStage.APPROVAL],
  [PermissionStage.APPROVAL]: [PermissionStage.APPROVAL]
};

export function allowedNextStages(current) {
  if (!current) return Object.values(PermissionStage);
  return NEXT_STAGES[current] || [current];
}

export function stageOptionsForEdit(current) {
  return allowedNextStages(current).map((stage) => ({
    label: stage,
    value: stage
  }));
}

// Permission and Compliance are one flow: same stages, typed subtasks and
// query rounds, stored in the same fields. Only the label and the chip differ,
// so ask this rather than comparing against a single kind.
const PERMISSION_FLOW_KINDS = [TaskKind.PERMISSION, TaskKind.COMPLIANCE];

export const usesPermissionFlowKind = (kind) =>
  PERMISSION_FLOW_KINDS.includes(kind || TaskKind.GENERAL);

export const usesPermissionFlow = (task) => usesPermissionFlowKind(task?.kind);

// Not "not General": a court case runs none of the flow above.
export const isLegalCaseTask = (task) => task?.kind === TaskKind.LEGAL_CASE;

// `tone` is a TaskCard Chip tone; `className` the chip on the task page.
const KIND_DISPLAY = {
  [TaskKind.GENERAL]: { label: 'General', tone: 'neutral', className: 'bg-gray-100 text-gray-700' },
  [TaskKind.PERMISSION]: {
    label: 'Permission',
    tone: 'purple',
    className: 'bg-purple-100 text-purple-800'
  },
  [TaskKind.COMPLIANCE]: {
    label: 'Compliance',
    tone: 'teal',
    className: 'bg-[#E6F1F2] text-[#2A565E]'
  },
  [TaskKind.LEGAL_CASE]: {
    label: 'Legal Court Case',
    tone: 'sky',
    className: 'bg-[#E0F2FE] text-[#0369A1]'
  }
};

const kindDisplay = (kind) => KIND_DISPLAY[kind] || KIND_DISPLAY[TaskKind.GENERAL];

export function kindLabel(kind) {
  return kindDisplay(kind).label;
}

export function kindChipTone(kind) {
  return kindDisplay(kind).tone;
}

export function kindChipClass(kind) {
  return kindDisplay(kind).className;
}

export function stageChipClass(stage) {
  switch (stage) {
    case PermissionStage.APPROVAL:
      return 'bg-green-100 text-green-800';
    case PermissionStage.QUERY_RAISED:
      return 'bg-amber-100 text-amber-800';
    case PermissionStage.SUBMISSION:
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

// Mirrors the backend SubTaskPermissionType. Three values, not four — Approval
// is the authority's act, so no subtask is ever assigned against it.
export const SubTaskPermissionType = {
  PREPARATION: 'Preparation',
  SUBMISSION: 'Submission',
  QUERY_RAISED: 'Query raised'
};

export const subtaskTypeOptions = Object.values(SubTaskPermissionType).map((type) => ({
  label: type,
  value: type
}));

// Query raised is only for reply subtasks created from Query rounds.
export function selectableSubtaskTypeOptions({ queryRaised = false } = {}) {
  return subtaskTypeOptions.filter((option) =>
    queryRaised
      ? option.value === SubTaskPermissionType.QUERY_RAISED
      : option.value !== SubTaskPermissionType.QUERY_RAISED
  );
}

export function subtaskTypeChipClass(type) {
  switch (type) {
    case SubTaskPermissionType.QUERY_RAISED:
      return 'bg-amber-100 text-amber-800';
    case SubTaskPermissionType.SUBMISSION:
      return 'bg-blue-100 text-blue-800';
    case SubTaskPermissionType.PREPARATION:
      return 'bg-indigo-100 text-indigo-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export const SUBTASK_TYPE_REQUIRED_MESSAGE =
  'Every subtask on this task needs a type';

export const COMPLETE_WITHOUT_APPROVAL_MESSAGE =
  'Stage is not Approval. Complete this task anyway?';

export const PERMISSION_END_BEFORE_START_MESSAGE =
  'End date cannot be before start date';

export const AUTHORITY_REQUIRED_MESSAGE = 'Authority is required';
export const APPLICATION_NUMBER_REQUIRED_MESSAGE = 'Application number is required';

export function permissionEndBeforeStart(start, end) {
  if (!start || !end) return false;
  return String(end) < String(start);
}

export function missingPermissionAuthority(authority) {
  return !String(authority || '').trim();
}

export function missingPermissionApplicationNumber(applicationNumber) {
  return !String(applicationNumber || '').trim();
}

export function priorPermissionType(type) {
  if (type === SubTaskPermissionType.SUBMISSION) return SubTaskPermissionType.PREPARATION;
  if (type === SubTaskPermissionType.QUERY_RAISED) return SubTaskPermissionType.SUBMISSION;
  return null;
}

export function isPermissionTypeCleared(subtask) {
  if (!subtask) return false;
  if (subtask.waived && String(subtask.waiveReason || '').trim()) return true;
  return subtask.status === TaskStatus.COMPLETED;
}

export function permissionStartBlockedMessage(type) {
  const prior = priorPermissionType(type);
  if (!prior) return '';
  return `Accept all ${prior} subtasks before starting ${type}`;
}

export function canStartPermissionSubtask(task, subtask) {
  if (!usesPermissionFlow(task) || !subtask) return true;
  const prior = priorPermissionType(subtask.permissionType);
  if (!prior) return true;
  const siblings = task.subTask || task.subtasks || [];
  const priors = siblings.filter(
    (row) =>
      row.permissionType === prior && String(row.id || '') !== String(subtask.id || '')
  );
  if (priors.length === 0) return true;
  return priors.every(isPermissionTypeCleared);
}

export function statusOptionsWhenPermissionStartBlocked(current, options) {
  return (options || []).filter(
    (option) => option.value === current || !isWorkStarted(option.value)
  );
}
